import { cacheBucket } from "$lib/utils/cacheBuckets";
import { recordTelemetryEvent } from "$lib/telemetry/telemetry";
import type { EventTags } from "$lib/telemetry/appsignal";
import { logger } from "$lib/utils/logger";

// Census snapshot cadence — hourly default to avoid frequent cache walks on
// the leak-prone WPE engine; override lower (e.g. 60000) in a test env.
const RECORD_INTERVAL_MS =
  Number(import.meta.env.VITE_STORAGE_METRICS_INTERVAL_MS) || 60 * 60 * 1000;

// The named buckets cacheBucket() classifies into, minus its "other" catch-all;
// keep in sync with cacheBuckets.ts.
const CENSUS_BUCKETS = ["video", "image", "font", "precache", "content"];

let recordInterval: NodeJS.Timeout | null = null;

function startStorageMetrics(): void {
  if (recordInterval) clearInterval(recordInterval);
  recordInterval = setInterval(record, RECORD_INTERVAL_MS);
  logger.info("[StorageMetrics] recording started.");
  record();
}

function cleanupStorageMetrics(): void {
  if (recordInterval) clearInterval(recordInterval);
  recordInterval = null;
  logger.info("[StorageMetrics] recording stopped.");
}

async function record(): Promise<void> {
  await recordStorageEstimate();
  await recordCacheSizes();
}

// storage.estimate() is unimplemented on WPE (added in Safari 17.0), so this
// no-ops on the fleet — kept for newer firmware; the census is the real signal.
async function recordStorageEstimate(): Promise<void> {
  try {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      recordFleetMeasurement(
        "frontend_estimate_usage_mb",
        bytesToMB(estimate.usage || 0)
      );
      recordFleetMeasurement(
        "frontend_estimate_quota_mb",
        bytesToMB(estimate.quota || 0)
      );
    } else {
      logger.info(
        "[StorageMetrics] Storage estimate not supported in this browser."
      );
    }
  } catch (error) {
    logger.error("[StorageMetrics] Failed to report storage usage:", error);
  }
}

// Walk the SW's caches from the page. Bytes come from Content-Length (a lower
// bound — reading every response body hourly is too costly on-device).
async function recordCacheSizes(): Promise<void> {
  if (typeof caches === "undefined") {
    logger.info("[StorageMetrics] CacheStorage not available.");
    return;
  }
  try {
    const names = await caches.keys();
    const byBucket: Record<string, { entries: number; bytes: number }> = {};
    const unreadableBuckets = new Set<string>();
    let totalBytes = 0;
    let totalEntries = 0;

    for (const name of names) {
      // Per-cache isolation: one corrupt/unreadable cache (a failure mode we
      // are specifically hunting) must not blank the whole census.
      try {
        const cache = await caches.open(name);
        const requests = await cache.keys();
        let bytes = 0;
        for (const request of requests) {
          // ignoreVary, matching how the SW reads these entries — a Vary'd
          // response (e.g. CORS video) must not census as 0 bytes.
          bytes += responseBytes(
            await cache.match(request, { ignoreVary: true })
          );
        }

        const bucket = (byBucket[cacheBucket(name)] ??= {
          entries: 0,
          bytes: 0,
        });
        bucket.entries += requests.length;
        bucket.bytes += bytes;
        totalBytes += bytes;
        totalEntries += requests.length;
      } catch (error) {
        unreadableBuckets.add(cacheBucket(name));
        logger.error(
          `[StorageMetrics] Skipping unreadable cache "${name}":`,
          error
        );
      }
    }

    // Named buckets 0-fill when genuinely absent so the distributions cover the
    // whole fleet like the totals. Skip a bucket with ANY unreadable cache — its
    // total is unknown, so neither a partial value nor a 0 may enter the fleet
    // sample. ("other" is unnamed, so it emits only when present.)
    const buckets = new Set([...CENSUS_BUCKETS, ...Object.keys(byBucket)]);
    for (const bucket of buckets) {
      if (unreadableBuckets.has(bucket)) continue;
      if (!(bucket in byBucket) && !CENSUS_BUCKETS.includes(bucket)) continue;
      const { entries, bytes } = byBucket[bucket] ?? { entries: 0, bytes: 0 };
      recordFleetMeasurement(
        "frontend_cache_size_perbucket_mb",
        bytesToMB(bytes),
        { bucket }
      );
      recordFleetMeasurement("frontend_cache_entries_perbucket", entries, {
        bucket,
      });
    }
    // Totals are fleet samples only when the whole census was readable; any
    // unreadable cache makes them incomplete subtotals, so omit them (the
    // readable per-bucket lines above still stand).
    if (unreadableBuckets.size === 0) {
      recordFleetMeasurement(
        "frontend_cache_size_total_mb",
        bytesToMB(totalBytes)
      );
      recordFleetMeasurement("frontend_cache_entries_total", totalEntries);
    }

    // Format numbers into the message string, not an object arg: WPE/JSC's
    // console renders object args as "[object Object]", which hides the sizes
    // in pulled device-log bundles (our only on-device way to read cache sizes,
    // since du is blocked and estimate() is unsupported).
    const bucketSummary = Object.entries(byBucket)
      .map(([b, v]) => `${b}=${bytesToMB(v.bytes)}MB/${v.entries}`)
      .join(" ");
    logger.info(
      `[StorageMetrics] cache census: total=${bytesToMB(totalBytes)}MB/${totalEntries} ${bucketSummary}`
    );
  } catch (error) {
    logger.error("[StorageMetrics] Failed to record cache sizes:", error);
  }
}

// displayId-free measurement: every screen's value aggregates into a fleet
// distribution under a few keys. A gauge here would last-write-wins to one screen;
// a displayId-tagged gauge blew the 10k/min key budget (one key per screen).
function recordFleetMeasurement(
  name: string,
  value: number,
  tags?: EventTags
): void {
  recordTelemetryEvent({
    name,
    metricType: "histogram",
    value,
    tags,
    omitDisplayId: true,
  });
}

function responseBytes(response: Response | undefined): number {
  const length = response?.headers.get("content-length");
  const bytes = length ? Number(length) : NaN;
  return Number.isFinite(bytes) ? bytes : 0;
}

// 0.1 MB resolution — whole-MB rounding flattened every bucket to ~1 MB.
function bytesToMB(bytes: number): number {
  return Math.round((bytes / 1024 / 1024) * 10) / 10;
}

export { startStorageMetrics, cleanupStorageMetrics, recordCacheSizes };
